/**
 * EsetSpecialistPage — the ESET endpoint-security specialist workspace
 * (DIGEMP-SPECIALIST-ESET-V2-FRONTEND-076). Route: /specialists/eset, agents_view gated.
 *
 * One read-only operator answer: "which Digital Employee covers ESET PROTECT endpoint security,
 * how good is its evidence right now, what is it actually allowed to read, what has it worked on,
 * what are its limits, and where does work escalate to a human?" Everything is projected from
 * existing agents_view endpoints: the agents list/detail (connection rows), the employee workspace
 * projection (current work, tickets, conversations, entitled tools), the audited run list, and —
 * for agents_manage holders only — the connector registry metadata.
 *
 * Nothing on this page performs, approves, simulates or schedules an action. Every next step is a
 * link into the existing run / ticket / conversation screens where the existing permission gates
 * already apply, so no gate is re-implemented, bypassed or weakened here.
 *
 * Truthfulness rules (same conventions as the attention inbox and the employee workspace):
 *   - an unknown or absent datum reads as unknown — never as "0" or a fabricated value;
 *   - connection-test evidence is the only real ESET readiness evidence at this revision, and it
 *     verifies the connection's authentication only — never a live device/detection read; evidence
 *     older than 25h (the backup-guardian default max age) and future-skewed timestamps (> 5 min)
 *     are labelled, never silently presented as verified success;
 *   - the supported-read list is the employee's actual entitled-tool projection, not a promise:
 *     a tool that is not in it is stated as unavailable, and any non-read ESET tool entitlement is
 *     disclosed explicitly instead of being hidden;
 *   - the employee's specialist-pack descriptor (specialist_packs, already embedded in the
 *     workspace projection) drives two more distinct buckets: actions that execute only through the
 *     approval path (execute_with_approval, with the pack's own deferred-after-approval limit lines
 *     rendered verbatim) and declared actions/capabilities that are NOT available (recommend — a
 *     forbidden tool, an unwired capability or a never-executable PROPOSED_WRITE contract). A
 *     missing descriptor reads as unknown, never as "none";
 *   - declared-but-unverified connector capabilities and the absent write/scan/isolation surface
 *     are stated as limitations, never as available features;
 *   - raw registry text and booleans stay in a collapsed technical drawer (secondary).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { agentsApi, agentRunsApi, agentConnectionsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META, drawnStatus } from "../components/EmployeeAvatar";
import { ExperienceTimeline } from "../components/EmployeeWorkspace";
import { parseUtc } from "../utils/utcDate";
import { conversationStatusLabel } from "../components/conversationStatusVocabulary";

// ─── Constants ───────────────────────────────────────────────────────────────

// How many of the specialist's most recent runs the evidence scan reads; the cap is disclosed in
// the UI instead of silently pretending the list is complete.
export const RUNS_SCAN_LIMIT = 50;

// Evidence older than this is stale — the same 25h default the backup guardian applies to backup
// freshness, reused here as the operator-facing freshness budget for connection evidence.
export const EVIDENCE_MAX_AGE_MS = 25 * 60 * 60 * 1000;

// A verification timestamp further in the future than this is clock skew: fail closed and label
// the evidence as unreliable instead of trusting it.
export const FUTURE_SKEW_MS = 5 * 60 * 1000;

// A run still in an active status but unchanged for this long reads as stalled (same rule as the
// employee workspace operator-attention strip).
export const STALL_AFTER_MS = 30 * 60 * 1000;

export const ESET_SYSTEM_TYPES = new Set(["eset", "eset_protect"]);

export const STALL_RUN_STATUSES = new Set(["pending", "analyzing", "diagnosing", "executing"]);

const FAILURE_RUN_STATUSES = new Set(["failed", "rejected"]);

const AWAITING_RUN_LABELS = {
  awaiting_technician_review: "נדרשת סקירת טכנאי",
  awaiting_approval: "נדרש אישור אנושי לפני המשך",
};

// The two conversation states that escalate to a person on this specialist's surface. Only the arm
// set is named here; the row's wording is "שיחה " + the shared conversation-status label.
const AWAITING_CONVERSATION_STATUSES = new Set(["awaiting_human", "awaiting_approval"]);

const EVIDENCE_CHIP = {
  green: "bg-green-50 border-green-200 text-green-700",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  red: "bg-red-50 border-red-200 text-red-700",
  gray: "bg-gray-50 border-gray-200 text-gray-600",
};

const EVIDENCE_DOT = {
  green: "bg-green-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  gray: "bg-gray-400",
};

// Entitled ESET read tools → operator label. An unmapped token passes through raw (mono, ltr)
// rather than being guessed into Hebrew.
const ESET_READ_LABELS = {
  eset_get_device_security_status: "מצב אבטחה של מכשיר",
  eset_list_device_groups: "רשימת קבוצות מכשירים",
  eset_list_devices: "רשימת מכשירים",
  eset_list_devices_in_group: "מכשירים בקבוצה",
  eset_get_device_details: "פרטי מכשיר",
};

// Declared connector capabilities → operator label. An unmapped token passes through raw.
const CAPABILITY_LABELS = {
  device_security_status: "מצב אבטחת מכשיר",
  threat_detections: "זיהויי איומים",
};

// The pack's dynamic limit line that names declared tools an approval cannot execute (117's
// no-handler disclosure). Matched by its stable backend-authored prefix and rendered verbatim.
const NO_HANDLER_PREFIX = "פעולות מוצהרות ללא מטפל ביצוע";

// Declared-but-unavailable ESET actions → operator label. An unmapped token passes through raw
// (mono, ltr) rather than being guessed into Hebrew.
const ESET_ACTION_LABELS = {
  eset_move_device: "העברת מכשיר לקבוצה אחרת (הצעה מתועדת)",
  eset_rename_device: "שינוי שם מכשיר (הצעה מתועדת)",
};

// The additive pack descriptor may arrive on the workspace payload or its agent projection, as a
// list or under a tolerant wrapper key; the domain token is matched the same way the M365 page does.
const ESET_PACK_ROOT_KEYS = ["specialist_packs", "specialist_pack_descriptors"];
const ESET_DOMAIN_TOKENS = new Set(["eset", "eset_protect"]);

// ─── Derivations (exported for focused tests) ────────────────────────────────

const text = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

function ageMsOf(iso, now) {
  const parsed = iso ? parseUtc(iso) : null;
  const ms = parsed ? parsed.getTime() : NaN;
  return Number.isFinite(ms) ? now - ms : null;
}

export function fmtAge(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "לפני רגע";
  if (minutes < 60) return `לפני ${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `לפני ${hours} שעות`;
  return `לפני ${Math.floor(hours / 24)} ימים`;
}

export function fmtAbs(iso) {
  const parsed = iso ? parseUtc(iso) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  try {
    return parsed.toLocaleString("he-IL");
  } catch {
    return null;
  }
}

// A status token is a failure only when one of its words is an explicit failure word
// (auth_error → failed); anything else non-empty stays unknown rather than being guessed.
const FAILURE_TOKEN_WORDS = new Set([
  "fail", "failed", "failure", "error", "denied", "invalid", "unauthorized", "rejected",
]);

export function isFailureStatusToken(raw) {
  return String(raw)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((word) => FAILURE_TOKEN_WORDS.has(word));
}

/**
 * The single derivation point for "how good is this connection's evidence right now". The status
 * token decides whether a verification was reported at all; the timestamp then decides whether a
 * reported verification may be trusted NOW (future skew → unreliable, age > budget → stale).
 * ok/success are the only verified tokens; an explicit failure word reads as failed; every other
 * token stays unknown — never guessed into success. This is the vocabulary the workspace shell
 * and the Guardian center share.
 */
export function connectionEvidence(connection, now = Date.now()) {
  const raw = text(connection?.last_test_status);
  if (!raw) {
    return {
      state: "never",
      label: "טרם נבדק",
      detail: "לא נרשמה בדיקת חיבור לחיבור זה",
      tone: "gray",
    };
  }
  const lowered = raw.toLowerCase();
  const verified = lowered === "ok" || lowered === "success";
  if (!verified && isFailureStatusToken(raw)) {
    return {
      state: "failed",
      label: "הבדיקה נכשלה",
      detail: "הבדיקה האחרונה של החיבור דיווחה כשלון",
      tone: "red",
      rawStatus: raw,
    };
  }
  if (!verified) {
    return {
      state: "unknown",
      label: "מצב בדיקה לא מזוהה",
      detail: "סטטוס הבדיקה מהשרת אינו מזוהה כהצלחה או ככשלון — לא ניתן להסיק ממנו מצב",
      tone: "amber",
      rawStatus: raw,
    };
  }
  const age = ageMsOf(connection?.last_tested_at, now);
  if (age == null) {
    return {
      state: "ok-no-timestamp",
      label: "אימות ללא חתימת זמן",
      detail: "הבדיקה דווחה כתקינה, אך ללא חתימת זמן לא ניתן להעריך את רעננותה",
      tone: "amber",
    };
  }
  if (age < -FUTURE_SKEW_MS) {
    return {
      state: "unreliable",
      label: "הראיה אינה אמינה",
      detail: "חתימת הזמן של הבדיקה עתידית ביחס לשעון — לא ניתן להסתמך עליה",
      tone: "red",
      ageMs: age,
    };
  }
  if (age > EVIDENCE_MAX_AGE_MS) {
    return {
      state: "stale",
      label: "הראיה ישנה",
      detail: `האימות האחרון ישן מ-${EVIDENCE_MAX_AGE_MS / 3_600_000} שעות — יש לבדוק מחדש לפני הסתמכות`,
      tone: "amber",
      ageMs: age,
    };
  }
  return {
    state: "ok",
    label: "אימות תקין",
    detail: "החיבור אומת בבדיקה האחרונה",
    tone: "green",
    ageMs: age,
  };
}

export function isEsetConnection(connection) {
  return !!connection && ESET_SYSTEM_TYPES.has(connection.system_type);
}

export function esetConnectionsOf(agentDetail) {
  return (agentDetail?.connections ?? []).filter(isEsetConnection);
}

/**
 * Profile-level signal only — the name/role/specialty token. Connection evidence is the primary
 * signal; this fallback keeps an ESET specialist visible (as a readiness gap) even before its
 * connection is configured.
 */
export function matchesEsetProfile(agent) {
  const fields = [agent?.name, agent?.role_title, ...(agent?.specialties ?? [])];
  return fields.some((value) => typeof value === "string" && /eset/i.test(value));
}

/**
 * Candidate specialists: connection-backed matches first, then profile-only matches. An agent
 * whose detail has not loaded yet can still be a profile match; it can never be silently dropped.
 */
export function buildSpecialistCandidates(agents = [], details = []) {
  const detailById = new Map(details.filter(Boolean).map((detail) => [detail.id, detail]));
  const candidates = [];
  for (const agent of agents) {
    const detail = detailById.get(agent.id) ?? null;
    const connections = esetConnectionsOf(detail);
    if (connections.length > 0) {
      candidates.push({ agent, detail, connections, matchedBy: "connection" });
      continue;
    }
    if (matchesEsetProfile(agent)) {
      candidates.push({ agent, detail, connections: [], matchedBy: "profile" });
    }
  }
  return candidates.sort((a, b) =>
    a.matchedBy === b.matchedBy ? 0 : a.matchedBy === "connection" ? -1 : 1
  );
}

/**
 * The employee's ACTUAL ESET read surface, projected from the workspace's entitled-tool list —
 * the same Tool Registry × entitlement projection the employee workspace renders. A tool that is
 * not listed here is not entitled for this employee; non-read ESET entitlements are disclosed per
 * tool by deriveEsetActionDepth instead of being hidden.
 */
export function deriveEsetReadSurface(tools = []) {
  const list = Array.isArray(tools) ? tools : [];
  return {
    reads: list.filter(
      (tool) =>
        typeof tool?.name === "string" &&
        tool.name.startsWith("eset_") &&
        tool.operation_type === "read_only"
    ),
  };
}

const tok = (value) =>
  typeof value === "string" ? value.trim().toLowerCase().replace(/[\s-]+/g, "_") : "";

function firstValue(raw, keys) {
  if (!raw || typeof raw !== "object") return undefined;
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function packListOf(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return null;
  for (const key of ["packs", "descriptors", "items"]) {
    if (Array.isArray(value[key])) return value[key];
  }
  return null;
}

/** The employee's ESET specialist-pack descriptor from the workspace projection, or null when the
 *  server did not supply one (unknown — never "no gated actions" / "nothing unavailable"). */
export function esetPackOf(workspace) {
  for (const source of [workspace, workspace?.agent]) {
    if (!source || typeof source !== "object") continue;
    for (const key of ESET_PACK_ROOT_KEYS) {
      const list = packListOf(source[key]);
      if (!list) continue;
      const match = list.find(
        (entry) =>
          entry &&
          typeof entry === "object" &&
          ESET_DOMAIN_TOKENS.has(
            tok(firstValue(entry, ["domain", "domain_key", "pack", "specialist_domain", "system_type", "connector"]))
          )
      );
      if (match) return match;
    }
  }
  return null;
}

/**
 * The three-way ESET action depth, from facts the page already fetched:
 *  - gated          : pack execute_with_approval — declared actions that run only through the
 *                     approval path (registry-authored ladder flags kept as-is);
 *  - deferredNotes  : the pack's own no-handler limit lines, rendered verbatim (an approval for a
 *                     declared tool with no handler is still rejected after approval);
 *  - unclassified   : entitled non-read ESET tools outside the pack's gated set — disclosed per
 *                     tool, never collapsed into a count and never silently hidden;
 *  - unavailable    : pack recommend — forbidden-at-this-tier tools, unwired declared capabilities
 *                     and never-executable PROPOSED_WRITE contracts.
 * Returns null when the workspace itself is unavailable (unknown, never "none").
 */
export function deriveEsetActionDepth(workspace) {
  if (!workspace || typeof workspace !== "object") return null;
  const pack = esetPackOf(workspace);
  const tools = Array.isArray(workspace?.capabilities?.tools) ? workspace.capabilities.tools : [];
  const nonReads = tools.filter(
    (tool) =>
      typeof tool?.name === "string" &&
      tool.name.startsWith("eset_") &&
      tool.operation_type !== "read_only"
  );
  const gated = Array.isArray(pack?.execute_with_approval) ? pack.execute_with_approval : [];
  const gatedNames = new Set(gated.map((entry) => entry?.name).filter(Boolean));
  return {
    packKnown: pack != null,
    gated,
    deferredNotes: (Array.isArray(pack?.limits) ? pack.limits : []).filter(
      (line) => typeof line === "string" && line.startsWith(NO_HANDLER_PREFIX)
    ),
    unclassified: nonReads.filter((tool) => !gatedNames.has(tool.name)),
    unavailable: Array.isArray(pack?.recommend) ? pack.recommend : [],
  };
}

/** Failure / age evidence derived from the same run list the outcome timeline below renders. */
export function deriveWorkEvidence(runs = [], now = Date.now()) {
  const list = Array.isArray(runs) ? runs : [];
  const failures = list.filter((run) => FAILURE_RUN_STATUSES.has(run?.status));
  const stalled = list.filter((run) => {
    if (!STALL_RUN_STATUSES.has(run?.status)) return false;
    const age = ageMsOf(run?.updated_at || run?.created_at, now);
    return age != null && age > STALL_AFTER_MS;
  });
  let newest = null;
  for (const run of list) {
    const reference = run?.updated_at || run?.created_at || run?.started_at;
    const age = ageMsOf(reference, now);
    if (age == null) continue;
    if (!newest || age < newest.ageMs) newest = { run, ageMs: age, at: reference };
  }
  const lastFailure = failures[0]
    ? { run: failures[0], ageMs: ageMsOf(failures[0].updated_at || failures[0].created_at, now) }
    : null;
  return { scanned: list.length, failures, stalled, newest, lastFailure };
}

/**
 * Items waiting on a human decision — projected from the workspace payload the page already
 * fetched. Returns null when the workspace itself is unavailable (unknown, not "none").
 */
export function deriveEscalationItems(workspace) {
  if (!workspace) return null;
  const items = [];
  const seenRuns = new Set();
  for (const ref of [workspace.current_work, ...(workspace.assigned_tickets ?? [])]) {
    if (!ref?.run_id || seenRuns.has(ref.run_id)) continue;
    const label = AWAITING_RUN_LABELS[ref.run_status];
    if (!label) continue;
    seenRuns.add(ref.run_id);
    const links = [{ to: `/agent-runs/${ref.run_id}`, label: "לריצה" }];
    if (ref.ticket_id) links.push({ to: `/tickets/${ref.ticket_id}?tab=ai-diagnosis`, label: "לכרטיס" });
    items.push({
      key: `run-${ref.run_id}`,
      label,
      detail: ref.ticket_number
        ? `קריאה #${ref.ticket_number}${ref.ticket_title ? ` · ${ref.ticket_title}` : ""}`
        : "משימה מתוך שיחה",
      at: ref.updated_at || ref.started_at || null,
      links,
    });
  }
  for (const conversation of workspace.recent_conversations ?? []) {
    if (!AWAITING_CONVERSATION_STATUSES.has(conversation?.status)) continue;
    items.push({
      key: `conversation-${conversation.id}`,
      label: `שיחה ${conversationStatusLabel(conversation.status)}`,
      detail: text(conversation.last_message_preview) || "",
      at: conversation.updated_at || null,
      links: [{ to: `/conversations/${conversation.id}`, label: "לשיחה" }],
    });
  }
  return items;
}

// ─── Small presentational pieces ─────────────────────────────────────────────

function shortId(value) {
  const raw = text(value);
  if (!raw) return null;
  return raw.length > 12 ? `${raw.slice(0, 8)}…` : raw;
}

function EvidencePill({ evidence }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${EVIDENCE_CHIP[evidence.tone] || EVIDENCE_CHIP.gray}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${EVIDENCE_DOT[evidence.tone] || EVIDENCE_DOT.gray}`} />
      {evidence.label}
    </span>
  );
}

function TechnicalDrawer({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="text-gray-500 hover:text-gray-700 underline decoration-dotted"
      >
        {open ? `הסתר ${label}` : label}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-gray-700 break-words">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Sections ────────────────────────────────────────────────────────────────

function EsetReadinessSection({ detail, isLoading, isError, onRetry }) {
  const connections = esetConnectionsOf(detail);
  const evidence = connections.map((connection) => ({
    connection,
    evidence: connectionEvidence(connection),
  }));
  const attention = evidence.filter(({ evidence: e }) => e.state !== "ok");
  return (
    <section aria-label="מוכנות ESET" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold text-gray-800">מוכנות ESET</h2>
        <p className="text-xs text-gray-500">
          מה ידוע כרגע על יכולת המומחה לעבוד מול ESET PROTECT — לפי ראיות בדיקת החיבור הרשומות.
        </p>
      </div>

      {!detail && isLoading && <p className="text-sm text-gray-400 italic">טוען את פרטי החיבורים…</p>}

      {!detail && !isLoading && isError && (
        <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          לא ניתן לטעון את פרטי החיבורים של המומחה — מצב החיבור אינו ידוע
          <button type="button" onClick={onRetry} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {detail && connections.length === 0 && (
        <div className="text-sm text-gray-600 space-y-1.5">
          <p className="font-medium text-gray-700">לא נמצא חיבור ESET למומחה זה.</p>
          <p className="text-xs text-gray-500">
            ללא חיבור אין ראיות מוכנות להעריך, והמומחה אינו יכול לקרוא דבר מ-ESET PROTECT.
            הגדרת החיבור מתבצעת באזור החיבורים שבעמוד העובד, בהרשאת ניהול סוכנים.
          </p>
        </div>
      )}

      {evidence.length > 0 && (
        <>
          <p className="text-xs text-gray-600">
            {attention.length === 0
              ? "כל חיבורי ESET של המומחה אומתו לאחרונה."
              : `לחיבור אחד או יותר אין אימות תקף כרגע (${attention.length} מתוך ${evidence.length}) — יש לבדוק מחדש.`}
          </p>
          <ul className="space-y-2">
            {evidence.map(({ connection, evidence: item }) => {
              const testedAt = fmtAbs(connection.last_tested_at);
              const age = item.ageMs != null ? fmtAge(item.ageMs) : null;
              const region = shortId(connection.tenant_id);
              const scopedCustomer = shortId(connection.customer_id);
              return (
                <li key={connection.id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">
                      {connection.display_name || "ESET PROTECT"}
                    </span>
                    <EvidencePill evidence={item} />
                    <span className="text-[11px] text-gray-500 whitespace-nowrap mr-auto">
                      {testedAt ? `נבדק: ${testedAt}${age ? ` (${age})` : ""}` : "ללא חתימת זמן"}
                    </span>
                  </div>
                  {item.detail && item.state !== "ok" && (
                    <p className="text-xs text-gray-600">{item.detail}</p>
                  )}
                  {item.rawStatus && (
                    <p className="text-xs text-gray-500">
                      תוצאת בדיקה מהשרת: <span className="font-mono" dir="ltr">{item.rawStatus}</span>
                    </p>
                  )}
                  <p className="text-xs text-gray-600">
                    {region ? (
                      <>
                        אזור ESET מוגדר: <span className="font-mono" dir="ltr">{region}</span>
                        {" "}— היעד שאליו מתבצעות הקריאות.
                      </>
                    ) : (
                      "טרם הוגדר אזור ESET (Region) לחיבור זה — עד להגדרתו הקריאות אינן מכוונות ליעד מוגדר."
                    )}
                  </p>
                  <p className="text-xs text-gray-600">
                    {scopedCustomer ? (
                      <>
                        החיבור מוגבל ללקוח מוגדר: <span className="font-mono" dir="ltr">{scopedCustomer}</span>
                        {" "}— הקריאות ממוקדות ללקוח זה.
                      </>
                    ) : (
                      "היקף הלקוח נגזר מהעובד שאליו החיבור משויך."
                    )}
                  </p>
                </li>
              );
            })}
          </ul>
          <p className="text-[11px] text-gray-400">
            בדיקת החיבור מאמתת את ההתחברות מול ESET בלבד — היא אינה מוכיחה קריאת מכשירים או זיהויים.
            ראיות מן הקריאות עצמן מופיעות ביומן הריצות שלמטה.
          </p>
        </>
      )}
    </section>
  );
}

function SupportedReadsSection({ workspace, isLoading, isError, onRetry }) {
  const surface = useMemo(
    () => deriveEsetReadSurface(workspace?.capabilities?.tools),
    [workspace]
  );
  return (
    <section aria-label="קריאות נתמכות" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold text-gray-800">קריאות נתמכות</h2>
        <p className="text-xs text-gray-500">
          מה המומחה מורשה לקרוא מ-ESET PROTECT — לפי הרשאות הכלים בפועל של העובד, לא לפי הצהרות.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-400 italic">טוען את הרשאות הכלים של המומחה…</p>}

      {!isLoading && isError && (
        <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          לא ניתן לטעון את הרשאות הכלים של המומחה — רשימת הקריאות הנתמכות אינה ידועה
          <button type="button" onClick={onRetry} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {!isLoading && !isError && surface.reads.length === 0 && (
        <div className="text-sm text-gray-600 space-y-1.5">
          <p className="font-medium text-gray-700">אין כלי קריאה של ESET בהרשאות העובד.</p>
          <p className="text-xs text-gray-500">
            ייתכן שהגישה ל-ESET טרם הופעלה עבור עובד זה. הרשאות הכלים מנוהלות דרך הרשאות העובד.
          </p>
        </div>
      )}

      {!isLoading && !isError && surface.reads.length > 0 && (
        <>
          <ul className="space-y-2">
            {surface.reads.map((tool) => (
              <li key={tool.name} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">
                    {ESET_READ_LABELS[tool.name] || <span className="font-mono" dir="ltr">{tool.name}</span>}
                  </span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full border bg-blue-50 border-blue-200 text-blue-700 whitespace-nowrap">
                    קריאה בלבד
                  </span>
                  {tool.approval_required && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full border bg-amber-50 border-amber-200 text-amber-800 whitespace-nowrap">
                      דורש אישור אנושי
                    </span>
                  )}
                  {tool.strong_approval_required && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full border bg-amber-50 border-amber-200 text-amber-800 whitespace-nowrap">
                      דורש אישור מוגבר
                    </span>
                  )}
                </div>
                {ESET_READ_LABELS[tool.name] && (
                  <p className="text-[11px] text-gray-500">
                    שם הכלי: <span className="font-mono" dir="ltr">{tool.name}</span>
                  </p>
                )}
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-600">
            כל קריאה מורצת בהקשר של קריאה/משימה של לקוח מאושר, וממוקדת למכשיר או לקבוצה מאושרת של
            אותו לקוח — אין קריאה חופשית בכל החשבון.
          </p>
        </>
      )}

      {!isLoading && !isError && (
        <p className="text-[11px] text-gray-400">
          כלי שאינו מופיע ברשימה אינו זמין לעובד זה — הרשימה משקפת את מצב ההרשאות הנוכחי ואינה הבטחה.
        </p>
      )}
    </section>
  );
}

function GatedActionsSection({ workspace, isLoading, isError, onRetry }) {
  const depth = useMemo(() => deriveEsetActionDepth(workspace), [workspace]);
  return (
    <section aria-label="פעולות המחייבות אישור" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold text-gray-800">פעולות המחייבות אישור</h2>
        <p className="text-xs text-gray-500">
          פעולות משנה-מצב שהמומחה אינו מריץ באופן עצמאי — לפי הגדרת החבילה (specialist pack)
          והרשאות העובד בפועל, לא לפי הצהרות.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-400 italic">טוען את הגדרת הפעולות של המומחה…</p>}

      {!isLoading && isError && (
        <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          לא ניתן לטעון את הגדרת הפעולות של המומחה — אילו פעולות מחייבות אישור אינו ידוע
          <button type="button" onClick={onRetry} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {!isLoading && !isError && depth && (
        <>
          {depth.gated.length > 0 && (
            <ul className="space-y-2">
              {depth.gated.map((entry) => (
                <li key={entry.name} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">
                      {ESET_ACTION_LABELS[entry.name] || <span className="font-mono" dir="ltr">{entry.name}</span>}
                    </span>
                    {entry.strong_approval_required ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-purple-50 border-purple-200 text-purple-700 whitespace-nowrap">
                        דורש אישור מוגבר
                      </span>
                    ) : entry.approval_required ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-orange-50 border-orange-200 text-orange-700 whitespace-nowrap">
                        דורש אישור אנושי
                      </span>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-50 border-gray-200 text-gray-600 whitespace-nowrap">
                        לא סומן כדורש אישור
                      </span>
                    )}
                  </div>
                  {ESET_ACTION_LABELS[entry.name] && (
                    <p className="text-[11px] text-gray-500">
                      שם הכלי: <span className="font-mono" dir="ltr">{entry.name}</span>
                    </p>
                  )}
                  {typeof entry.description === "string" && entry.description.trim() && (
                    <p className="text-[11px] text-gray-500" dir="ltr">{entry.description.slice(0, 160)}</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {depth.deferredNotes.map((line) => (
            <p key={line} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              {line}
            </p>
          ))}

          {depth.unclassified.length > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1">
              <p>
                {depth.packKnown
                  ? "לעובד מורשות גם פעולות שאינן קריאה שאינן מופיעות בהגדרת החבילה — מצב האישור שלהן אינו מסווג:"
                  : "לעובד מורשות פעולות שאינן קריאה, והגדרת החבילה (specialist pack) לא נטענה — אילו מהן דורשות אישור אינו ידוע:"}
              </p>
              <ul className="list-disc pr-5 space-y-0.5">
                {depth.unclassified.map((tool) => (
                  <li key={tool.name}>
                    <span className="font-mono" dir="ltr">{tool.name}</span>
                    {tool.strong_approval_required
                      ? " — מסומן כדורש אישור מוגבר"
                      : tool.approval_required
                        ? " — מסומן כדורש אישור אנושי"
                        : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {depth.packKnown && depth.gated.length === 0 && depth.unclassified.length === 0 && (
            <p className="text-sm text-gray-600">
              בהגדרת החבילה לא מוצהרות פעולות משנה-מצב עם מסלול אישור — כל שינוי מצב שאינו מופיע
              כאן אינו זמין דרך המומחה.
            </p>
          )}

          {!depth.packKnown && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
              הגדרת החבילה (specialist pack) לא התקבלה מהשרת — אילו פעולות משנה-מצב מחייבות אישור
              אינו ידוע; אין להסיק שאין.
            </p>
          )}

          <p className="text-[11px] text-gray-400">
            המומחה אינו מאשר פעולות לעצמו, אינו מעניק הרשאות ואינו מנמיך רמת סיכון — גם לא דרך מסך
            זה.
          </p>
        </>
      )}
    </section>
  );
}

function UnavailableActionsSection({ workspace, isLoading, isError, onRetry }) {
  const depth = useMemo(() => deriveEsetActionDepth(workspace), [workspace]);
  return (
    <section aria-label="פעולות שאינן זמינות" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold text-gray-800">פעולות שאינן זמינות</h2>
        <p className="text-xs text-gray-500">
          פעולות ויכולות מוצהרות שאינן זמינות לעובד זה — הרשאה חסרה, יכולות שטרם חוברו לכלי, או
          הצעות כתיבה מתועדות שאינן ניתנות לביצוע.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-400 italic">טוען את הפעולות המוצהרות של המומחה…</p>}

      {!isLoading && isError && (
        <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          לא ניתן לטעון את הפעולות המוצהרות של המומחה — אילו פעולות אינן זמינות אינו ידוע
          <button type="button" onClick={onRetry} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {!isLoading && !isError && depth && depth.packKnown && depth.unavailable.length > 0 && (
        <>
          <ul className="space-y-2">
            {depth.unavailable.map((entry) => {
              const label = ESET_ACTION_LABELS[entry.name] || CAPABILITY_LABELS[entry.name] || null;
              return (
                <li key={entry.name} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">
                      {label || <span className="font-mono" dir="ltr">{entry.name}</span>}
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full border bg-indigo-50 border-indigo-200 text-indigo-700 whitespace-nowrap">
                      המלצה בלבד — אינה זמינה לעובד
                    </span>
                  </div>
                  {label && (
                    <p className="text-[11px] text-gray-500">
                      שם הפעולה: <span className="font-mono" dir="ltr">{entry.name}</span>
                    </p>
                  )}
                  {typeof entry.description === "string" && entry.description.trim() && (
                    <p className="text-[11px] text-gray-600">{entry.description.slice(0, 220)}</p>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="text-[11px] text-gray-400">
            הפעולות ברשימה זו אינן ניתנות להרצה על-ידי המומחה — הן מועברות להחלטת טכנאי מורשה.
          </p>
        </>
      )}

      {!isLoading && !isError && depth && depth.packKnown && depth.unavailable.length === 0 && (
        <p className="text-sm text-gray-600">בהגדרת החבילה לא דווחו פעולות מוצהרות שאינן זמינות לעובד זה.</p>
      )}

      {!isLoading && !isError && depth && !depth.packKnown && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          הגדרת החבילה (specialist pack) לא התקבלה מהשרת — אילו פעולות מוצהרות אינן זמינות אינו
          ידוע; אין להסיק שאין.
        </p>
      )}
    </section>
  );
}

function WorkEvidenceSection({ workspace, workspaceLoading, workspaceError, onWorkspaceRetry, runs, runsLoading, runsError, onRunsRetry }) {
  const evidence = useMemo(() => deriveWorkEvidence(runs), [runs]);
  const currentWork = workspace?.current_work ?? null;
  return (
    <section aria-label="עבודה אחרונה וראיות" className="space-y-4">
      <div className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
        <h2 className="text-base font-semibold text-gray-800">עבודה אחרונה של המומחה</h2>
        <p className="text-xs text-gray-500">
          נסרקו {RUNS_SCAN_LIMIT} הריצות האחרונות של המומחה; הפירוט המלא מוצג ביומן התוצאות שלמטה.
        </p>

        {workspaceLoading && <p className="text-sm text-gray-400 italic">טוען את מצב העבודה הנוכחי…</p>}
        {!workspaceLoading && workspaceError && (
          <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            לא ניתן לטעון את מצב העבודה הנוכחי של המומחה
            <button type="button" onClick={onWorkspaceRetry} className="mr-2 underline">נסה שוב</button>
          </div>
        )}
        {!workspaceLoading && !workspaceError && !currentWork && (
          <p className="text-sm text-gray-400 italic">פנוי — אין משימה פעילה.</p>
        )}
        {!workspaceLoading && !workspaceError && currentWork && (
          <div className="text-sm space-y-1">
            <div className="font-medium text-gray-800">
              {currentWork.ticket_number ? `#${currentWork.ticket_number} · ` : ""}
              {currentWork.ticket_title || "משימה מתוך שיחה"}
            </div>
            <div className="text-xs text-amber-700">{currentWork.run_status_label}</div>
            <div className="text-xs text-gray-400">מאז {fmtAbs(currentWork.started_at) || "זמן לא ידוע"}</div>
            <div className="flex gap-2 text-xs">
              <Link to={`/agent-runs/${currentWork.run_id}`} className="text-blue-600 hover:underline">צפה בריצה</Link>
              {currentWork.ticket_id && (
                <Link to={`/tickets/${currentWork.ticket_id}?tab=ai-diagnosis`} className="text-blue-600 hover:underline">לכרטיס</Link>
              )}
              {currentWork.conversation_id && (
                <Link to={`/conversations/${currentWork.conversation_id}`} className="text-blue-600 hover:underline">לשיחה</Link>
              )}
            </div>
          </div>
        )}

        <div data-testid="eset-work-evidence" className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 text-xs text-gray-700 space-y-1">
          {runsLoading && <p className="text-gray-400 italic">טוען ראיות מריצות המומחה…</p>}
          {!runsLoading && runsError && (
            <div role="alert" className="text-red-600">
              לא ניתן לטעון את ריצות המומחה — מצב הראיות אינו ידוע
              <button type="button" onClick={onRunsRetry} className="mr-2 underline">נסה שוב</button>
            </div>
          )}
          {!runsLoading && !runsError && (
            <>
              <p>
                ריצות בסריקה: <span className="font-medium">{evidence.scanned}</span>
                {evidence.scanned >= RUNS_SCAN_LIMIT && <span className="text-gray-400"> (ייתכנו נוספות מעבר לחלון הסריקה)</span>}
              </p>
              <p>
                הראיה האחרונה מריצה: {evidence.newest
                  ? <>{fmtAge(evidence.newest.ageMs) || "זמן לא ידוע"} ({fmtAbs(evidence.newest.at) || "ללא חתימת זמן"}){" "}
                      <Link to={`/agent-runs/${evidence.newest.run.id}`} className="text-blue-600 hover:underline">לריצה</Link></>
                  : "אין ריצות רשומות"}
              </p>
              <p>
                כשלים בסריקה: <span className="font-medium">{evidence.failures.length}</span>
                {evidence.failures.length === 0
                  ? " — לא נמצאו ריצות שנכשלו או נדחו בחלון זה"
                  : <> — {evidence.lastFailure && (
                      <>האחרון {fmtAge(evidence.lastFailure.ageMs) || "בזמן לא ידוע"}; </>
                    )}הפירוט מוצג ביומן התוצאות שלמטה</>}
              </p>
              {evidence.stalled.map((run) => (
                <p key={run.id} className="text-amber-800">
                  ריצה פעילה ללא התקדמות מעל 30 דקות:{" "}
                  <Link to={`/agent-runs/${run.id}`} className="text-blue-600 hover:underline">
                    {run.ticket_number ? `קריאה #${run.ticket_number}` : "לריצה"}
                  </Link>
                </p>
              ))}
            </>
          )}
        </div>
      </div>

      <ExperienceTimeline runs={runs} isLoading={runsLoading} isError={runsError} />
    </section>
  );
}

function KnownLimitsSection({ canManage, connectorTypes, isLoading, isError, onRetry }) {
  const definition = (connectorTypes ?? []).find(
    (entry) => entry?.connector_id === "eset_protect" || entry?.system_type === "eset_protect"
  );
  return (
    <section aria-label="מגבלות ידועות" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold text-gray-800">מגבלות ידועות</h2>
        <p className="text-xs text-gray-500">מה המומחה יכול ובעיקר אינו יכול לעשות מול ESET PROTECT.</p>
      </div>

      {!canManage && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          פרטי המגבלות של סוג החיבור זמינים לבעלי הרשאת ניהול סוכנים (agents_manage).
          לא בוצעה קריאה לשרת, ולכן המגבלות המדויקות אינן מוצגות כאן.
        </p>
      )}

      {canManage && isLoading && <p className="text-sm text-gray-400 italic">טוען את הגדרות סוג החיבור…</p>}

      {canManage && !isLoading && isError && (
        <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          לא ניתן לטעון את הגדרות סוג החיבור ESET
          <button type="button" onClick={onRetry} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {canManage && !isLoading && !isError && !definition && (
        <p className="text-sm text-gray-600">
          סוג החיבור ESET אינו מופיע במרשם המחברים — לא ניתן להציג מגבלות מדויקות.
        </p>
      )}

      {canManage && !isLoading && !isError && definition && (
        <>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            {definition.read_only && (
              <span className="px-2 py-0.5 rounded-full border bg-blue-50 border-blue-200 text-blue-700">
                קריאה בלבד
              </span>
            )}
            {definition.implemented === false && (
              <span className="px-2 py-0.5 rounded-full border bg-amber-50 border-amber-200 text-amber-800">
                טרם מומש — אין כלי ריצה רשומים
              </span>
            )}
            {definition.supports_test && (
              <span className="px-2 py-0.5 rounded-full border bg-teal-50 border-teal-200 text-teal-700">
                בדיקת חיבור נתמכת
              </span>
            )}
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-600">יכולות מוצהרות (טרם אומתו):</p>
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              {(definition.capabilities ?? []).map((token) => (
                <span key={token} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-gray-50 border-gray-200 text-gray-700">
                  {CAPABILITY_LABELS[token] || <span className="font-mono" dir="ltr">{token}</span>}
                  <span className="text-gray-400">· טרם אומת</span>
                </span>
              ))}
              {(definition.capabilities ?? []).length === 0 && (
                <span className="text-gray-400">השרת לא דיווח יכולות מוצהרות</span>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-600">
            אין כלי סריקה, בידוד מכשיר, מחיקה, שינוי מדיניות הגנה, פקודה מרחק או תיקון — כל פעולה
            משנה-מצב מול ESET PROTECT אינה זמינה לעובד הדיגיטלי ומחייבת טכנאי אנושי.
          </p>

          <TechnicalDrawer label="הגדרת החיבור הגולמית">
            <p className="font-medium mb-1">read_only={String(definition.read_only)} · implemented={String(definition.implemented)} · supports_test={String(definition.supports_test)}</p>
            <p className="font-medium mb-1" dir="ltr">{definition.category} · {definition.scope} · target={definition.target_selection}</p>
            <p className="whitespace-pre-wrap break-words" dir="ltr">{definition.description}</p>
          </TechnicalDrawer>
        </>
      )}
    </section>
  );
}

function EscalationSection({ workspace, workspaceLoading, workspaceError, agentId }) {
  const items = workspaceError ? null : deriveEscalationItems(workspace);
  return (
    <section aria-label="נתיב הסלמה" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold text-gray-800">נתיב הסלמה</h2>
        <p className="text-xs text-gray-500">מתי ואיך עוברת העבודה מהמומחה הדיגיטלי לטכנאי אנושי.</p>
      </div>

      <ol className="list-decimal pr-5 space-y-1.5 text-sm text-gray-700">
        <li>אבחון קריאה-בלבד על-ידי המומחה — איסוף מצב אבטחה וזיהויים, ללא שינוי דבר.</li>
        <li>
          כל פעולה משנה-מצב (סריקה יזומה, בידוד מכשיר, הסגר או מחיקת קובץ, שינוי מדיניות הגנה)
          אינה נתמכת בכלי העובד — היא מבוצעת על-ידי טכנאי אנושי בלבד.
        </li>
        <li>החלטות הממתינות לאדם (סקירת טכנאי, אישור ריצה, מענה בשיחה) מטופלות במסכים הקיימים:</li>
      </ol>

      <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-1.5">
        {workspaceLoading && <p className="text-sm text-gray-400 italic">טוען פריטים הממתינים להחלטה אנושית…</p>}
        {!workspaceLoading && workspaceError && (
          <p role="alert" className="text-sm text-red-600">
            לא ניתן לטעון את הפריטים הממתינים להחלטה — מצבם אינו ידוע
          </p>
        )}
        {!workspaceLoading && !workspaceError && items && items.length === 0 && (
          <p className="text-sm text-gray-500 italic">אין פריטים הממתינים להחלטה אנושית כרגע.</p>
        )}
        {!workspaceLoading && !workspaceError && items && items.length > 0 && (
          <ul className="space-y-1.5">
            {items.map((item) => (
              <li key={item.key} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-gray-800 whitespace-nowrap">{item.label}</span>
                {item.detail && <span className="text-xs text-gray-600 truncate max-w-full">{item.detail}</span>}
                <span className="text-[11px] text-gray-400 whitespace-nowrap">
                  {fmtAbs(item.at) || "זמן לא ידוע"}
                </span>
                <span className="flex gap-2 text-xs mr-auto whitespace-nowrap">
                  {item.links.map((link) => (
                    <Link key={link.to} to={link.to} className="text-blue-600 hover:underline">{link.label}</Link>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap gap-3 text-xs">
        <Link to="/tickets" className="text-blue-600 hover:underline">פתיחת קריאה לטכנאי</Link>
        <Link to="/attention-inbox" className="text-blue-600 hover:underline">תיבת תשומת הלב</Link>
        <Link to="/agent-runs" className="text-blue-600 hover:underline">כל הריצות</Link>
        <Link to={`/agents/${agentId}`} className="text-blue-600 hover:underline">עמוד העובד — חיבורים והרשאות</Link>
      </div>

      <p className="text-[11px] text-gray-400">
        העובד הדיגיטלי אינו מאשר פעולות לעצמו, אינו מעניק הרשאות ואינו מנמיך רמת סיכון — גם לא דרך
        מסך זה. מסך זה הוא צפייה בלבד.
      </p>
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function EsetSpecialistPage() {
  const { can, loading: permsLoading } = useAgentPermissions();
  const canView = can("agents_view");
  const canManage = can("agents_manage");
  const [selectedId, setSelectedId] = useState(null);

  const agentsQuery = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
    enabled: canView,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);

  const detailResults = useQueries({
    queries: agents.map((agent) => ({
      queryKey: ["agent", agent.id],
      queryFn: () => agentsApi.get(agent.id),
      enabled: canView,
      staleTime: 30_000,
      refetchInterval: 60_000,
    })),
  });
  const details = detailResults.map((result) => result.data).filter(Boolean);
  const detailsLoading = detailResults.some((result) => result.isLoading);
  const detailsFailed = detailResults.filter((result) => result.isError).length;

  const candidates = useMemo(() => buildSpecialistCandidates(agents, details), [agents, details]);
  const active = candidates.find((candidate) => candidate.agent.id === selectedId) ?? candidates[0] ?? null;
  const activeId = active?.agent.id ?? null;
  // The presence shown for the active specialist. A disabled employee is offline — the same rule
  // EmployeeAvatar applies to its own dot, read from the component rather than restated, so the
  // printed "· <status>" label cannot contradict the dot beside it or the "· מושבת" that follows it.
  const activeShownStatus = active
    ? drawnStatus(active.agent.availability_status, active.agent.is_enabled)
    : null;
  const activeIndex = agents.findIndex((agent) => agent.id === activeId);
  const activeDetailResult = activeIndex >= 0 ? detailResults[activeIndex] : null;

  const workspaceQuery = useQuery({
    queryKey: ["agent-workspace", activeId],
    queryFn: () => agentsApi.workspace(activeId),
    enabled: canView && !!activeId,
    refetchInterval: 15_000,
  });
  const runsQuery = useQuery({
    queryKey: ["agent-runs", "eset-specialist", activeId, RUNS_SCAN_LIMIT],
    queryFn: () => agentRunsApi.listByAgent(activeId, { limit: RUNS_SCAN_LIMIT }),
    enabled: canView && !!activeId,
    refetchInterval: 15_000,
  });
  const connectorTypesQuery = useQuery({
    queryKey: ["agentConnectorTypes"],
    queryFn: () => agentConnectionsApi.connectorTypes(),
    enabled: canView && canManage,
    staleTime: 60_000,
  });

  if (permsLoading) return <div className="p-6 text-gray-400">טוען הרשאות…</div>;
  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות במרחב המומחה של ESET</div>;
  }

  const discoveryPending = agentsQuery.isLoading || (agents.length > 0 && detailsLoading);
  const runs = runsQuery.data ?? [];
  const activeDetail = active?.detail ?? null;

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6">
      <header className="space-y-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-xl font-bold text-gray-800">מומחה אבטחת קצה · ESET PROTECT</h1>
          <span className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-50 border-gray-200 text-gray-600">
            צפייה בלבד
          </span>
        </div>
        <p className="text-sm text-gray-500">
          מרחב העבודה התפעולי של העובד הדיגיטלי שמטפל באבטחת נקודות קצה מול ESET PROTECT: מוכנות
          החיבור, קריאות מורשות, עבודה אחרונה וראיות, מגבלות ידועות ונתיב הסלמה לטכנאי.
        </p>
      </header>

      {agentsQuery.isError && (
        <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          לא ניתן לטעון את רשימת העובדים הדיגיטליים
          <button type="button" onClick={() => agentsQuery.refetch()} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {!agentsQuery.isError && candidates.length === 0 && discoveryPending && (
        <p className="text-sm text-gray-400 italic">מאתר עובדים דיגיטליים ומאמת חיבורים…</p>
      )}

      {!agentsQuery.isError && candidates.length === 0 && !discoveryPending && (
        <section aria-label="אין מומחה ESET" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
          <h2 className="text-base font-semibold text-gray-800">לא נמצא עובד דיגיטלי עם חיבור ESET</h2>
          <p className="text-sm text-gray-600">
            מומחה מזוהה לפי חיבור ESET PROTECT מוגדר, או לפי שם / תפקיד / התמחות שמזכירים ESET.
            כרגע אין עובד שעונה לאף אחד מהשניים.
          </p>
          {detailsFailed > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              פרטי החיבורים של {detailsFailed} עובדים לא נטענו — ייתכן שגם הם מומחי ESET, ולכן
              הרשימה עשויה להיות חלקית.
            </p>
          )}
          <div className="flex flex-wrap gap-3 text-xs">
            <Link to="/agents" className="text-blue-600 hover:underline">לכל העובדים הדיגיטליים</Link>
            <Link to="/employee-builder" className="text-blue-600 hover:underline">בניית עובד חדש</Link>
          </div>
          <p className="text-xs text-gray-500">
            חיבור ESET PROTECT מוגדר בעמוד העובד, באזור החיבורים, על-ידי בעל הרשאת ניהול סוכנים.
          </p>
        </section>
      )}

      {candidates.length > 0 && active && (
        <>
          {candidates.length > 1 && (
            <section aria-label="בחירת מומחה" className="bg-white rounded-xl border shadow-sm p-3 space-y-2">
              <p className="text-xs text-gray-500">
                נמצאו {candidates.length} עובדים עם זיקת ESET — בחרו מומחה להצגה:
              </p>
              <div className="flex flex-wrap gap-2">
                {candidates.map((candidate) => (
                  <button
                    key={candidate.agent.id}
                    type="button"
                    aria-pressed={candidate.agent.id === activeId}
                    onClick={() => setSelectedId(candidate.agent.id)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm ${
                      candidate.agent.id === activeId
                        ? "border-blue-300 bg-blue-50 text-blue-800"
                        : "border-gray-200 hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <EmployeeAvatar
                      name={candidate.agent.name}
                      avatarKey={candidate.agent.avatar_key}
                      avatarColor={candidate.agent.avatar_color}
                      size="sm"
                    />
                    <span>{candidate.agent.name}</span>
                    <span className="text-[10px] text-gray-400">
                      {candidate.matchedBy === "connection" ? "לפי חיבור" : "לפי פרופיל"}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          <div className="bg-white rounded-xl border shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-3">
              <EmployeeAvatar
                name={active.agent.name}
                avatarKey={active.agent.avatar_key}
                avatarColor={active.agent.avatar_color}
                status={active.agent.availability_status}
                isEnabled={active.agent.is_enabled}
                size="lg"
                showStatus
              />
              <div className="space-y-0.5">
                <h2 className="text-lg font-semibold text-gray-800">{active.agent.name}</h2>
                <p className="text-xs text-gray-500">
                  {active.agent.role_title || "עובד דיגיטלי"}
                  {activeShownStatus && STATUS_META[activeShownStatus]
                    ? ` · ${STATUS_META[activeShownStatus].label}`
                    : ""}
                  {active.agent.is_enabled === false ? " · מושבת" : ""}
                </p>
                {(active.agent.specialties ?? []).length > 0 && (
                  <p className="text-xs text-gray-600">
                    התמחות מוצהרת: {active.agent.specialties.join(" · ")}
                  </p>
                )}
              </div>
              <Link
                to={`/agents/${active.agent.id}`}
                className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50 md:mr-auto"
              >
                לעמוד העובד המלא
              </Link>
            </div>
          </div>

          <EsetReadinessSection
            detail={activeDetail}
            isLoading={!!activeDetailResult?.isLoading}
            isError={!!activeDetailResult?.isError}
            onRetry={() => activeDetailResult?.refetch()}
          />

          <SupportedReadsSection
            workspace={workspaceQuery.data ?? null}
            isLoading={workspaceQuery.isLoading}
            isError={workspaceQuery.isError}
            onRetry={() => workspaceQuery.refetch()}
          />

          <GatedActionsSection
            workspace={workspaceQuery.data ?? null}
            isLoading={workspaceQuery.isLoading}
            isError={workspaceQuery.isError}
            onRetry={() => workspaceQuery.refetch()}
          />

          <UnavailableActionsSection
            workspace={workspaceQuery.data ?? null}
            isLoading={workspaceQuery.isLoading}
            isError={workspaceQuery.isError}
            onRetry={() => workspaceQuery.refetch()}
          />

          <WorkEvidenceSection
            workspace={workspaceQuery.data ?? null}
            workspaceLoading={workspaceQuery.isLoading}
            workspaceError={workspaceQuery.isError}
            onWorkspaceRetry={() => workspaceQuery.refetch()}
            runs={runs}
            runsLoading={runsQuery.isLoading}
            runsError={runsQuery.isError}
            onRunsRetry={() => runsQuery.refetch()}
          />

          <KnownLimitsSection
            canManage={canManage}
            connectorTypes={connectorTypesQuery.data}
            isLoading={connectorTypesQuery.isLoading}
            isError={connectorTypesQuery.isError}
            onRetry={() => connectorTypesQuery.refetch()}
          />

          <EscalationSection
            workspace={workspaceQuery.data ?? null}
            workspaceLoading={workspaceQuery.isLoading}
            workspaceError={workspaceQuery.isError}
            agentId={active.agent.id}
          />
        </>
      )}
    </div>
  );
}
